import {
  DEFAULT_CHANNEL_SORT,
  DEFAULT_CHANNEL_TAB,
  isChannelTab,
  normalizeChannelSort,
  type ChannelTab,
  type ChannelVideoSort,
} from '../channel/tabs.ts';
import { DEFAULT_SETTINGS_TAB, isSettingsTab, type SettingsTab } from '../settings/tabs.ts';
import { normalizeUiLocale, type UiLocaleId } from '../i18n/locale.ts';
import { parseUrl, queryParam } from '../instance/urls.ts';
import type { SubtitlePref } from '../model/playback.ts';
import {
  DEFAULT_SEARCH_QUERY,
  normalizeSearchDate,
  normalizeSearchDuration,
  normalizeSearchFeatures,
  normalizeSearchPage,
  normalizeSearchSort,
  normalizeSearchType,
  SEARCH_FIRST_PAGE,
  type SearchQuery,
} from '../search/query.ts';
import { parseTimeParamSeconds } from '../text/timecode.ts';

/**
 * The in-app route model — §8's "URL scheme", the half of it that is a pure function.
 *
 * It lives in core rather than in the web router for the same reason `parseTimestampParam` does
 * (§8): an Android deep link has to land on the identical screen from the identical URL, and a
 * router written in React would have to be re-derived there from prose. What stays on the platform
 * is `history`/`popstate` and the back stack — the mechanism, not the meaning.
 *
 * **Both halves of §8's table are here**, and the two halves are implemented
 * differently on purpose:
 *
 * - A form that **carries everything the target needs** is *canonicalised*: `/shorts/ID`,
 *   `/live/ID`, `/watch/ID` and a bare 11-character ID all parse straight to the watch arm, the
 *   same way `/feed/library` parses to `playlists` and `/search?q=` to the search arm.
 *   (`/embed/ID` was among them until it got its own page — see the `embed` arm.)
 *   Nothing has to be looked up, so there is nothing to redirect *through* — and
 *   {@link canonicalHref} is what rewrites the address bar afterwards, once, for every non-canonical
 *   spelling at the same time.
 * - A form that **names something the API has to identify** keeps its own arm and its own screen,
 *   because reading it costs a request that can fail: `channelName` (`/@handle`, `/c/NAME`,
 *   `/user/NAME`) and `clip`. `post` is a third arm with no request at all — §8 wants it to say
 *   community posts are unsupported rather than 404.
 *
 * The alternative — a `{kind:'redirect', to}` arm — was rejected: it makes `Route` recursive, and
 * every `switch` over it in the app and on Android then has to handle a route that is not a page.
 *
 * §8's precedence is why the order of the blocks in {@link parseRoute} is load-bearing: the bare-ID
 * rule is a catch-all, so it is **last** and validated against {@link isVideoId} exactly.
 */

/**
 * The parameters YouTube's embed player reads off its own URL, as far as this player can honour
 * them: `/embed/ID?autoplay=1&mute=1&start=30&end=90`.
 *
 * **They belong to the route rather than to a query somebody reads off the address**, and that is
 * forced rather than chosen: `canonicalHref` rewrites the address bar to `formatRoute`'s spelling,
 * so a parameter with no field here is stripped a moment after the page loads and is then gone from
 * a reload, from a copied address and from anything that reads the URL later.
 *
 * Every field has an unset value that formats to nothing, which is what keeps the fixed point: a URL
 * that spells out a parameter's own default (`controls=1`, `color=red`) canonicalises to one that
 * does not mention it, exactly as a `?si=` does.
 */
export interface EmbedOptions {
  /**
   * `autoplay=1`. **Absent means no**, and that overrides the Playback tab's "Start playing on
   * arrival": the setting is a statement about pages the reader opened, and an embed is a video
   * somebody else put on a page of their own.
   */
  readonly autoplay: boolean;
  /** `mute=1` — start silent, whatever level the reader last left behind. */
  readonly mute: boolean;
  /** `loop=1` — begin again at the end, or begin the sequence `playlist` names again. */
  readonly loop: boolean;
  /** `end=<seconds>` — stop here instead of at the video's own end. */
  readonly endSec: number | null;
  /** `playlist=ID,ID` — what to play after this one, in order. See {@link embedEnding}. */
  readonly playlist: readonly string[];
  /** `controls=0` — no player bar. The picture still answers a click and the keyboard. */
  readonly controls: boolean;
  /** `fs=0` — no fullscreen button. */
  readonly fullscreen: boolean;
  /** `disablekb=1` — the keymap is not bound at all. */
  readonly keyboard: boolean;
  /** `cc_load_policy=1` — subtitles on from the first frame. */
  readonly captions: boolean;
  /** `cc_lang_pref=<tag>` — which language, matched against whatever tracks the video has. */
  readonly captionLanguage: string | null;
  /** `hl=<tag>` — the language the player's own words are in, when this build has that catalogue. */
  readonly uiLanguage: UiLocaleId | null;
  /**
   * `color=white` paints the played part of the bar white. `red` is both YouTube's default and the
   * colour this player's bar already wears, so it is read as no request at all.
   */
  readonly progressColor: 'white' | null;
}

/** Every parameter unset — what `/embed/ID` alone means, and the value a link builder passes. */
export const DEFAULT_EMBED_OPTIONS: EmbedOptions = {
  autoplay: false,
  mute: false,
  loop: false,
  endSec: null,
  playlist: [],
  controls: true,
  fullscreen: true,
  keyboard: true,
  captions: false,
  captionLanguage: null,
  uiLanguage: null,
  progressColor: null,
};

/**
 * The playlist an embed is walking — `?list=PL…`, and which entry of it is on screen.
 *
 * Separate from {@link EmbedOptions} because it is not a *parameter* of the player: the options say
 * how one video is presented, and this says which video is playing at all. It is also the only part
 * of an embed address that can require a request before the page knows what it is showing.
 *
 * **`index` is the playlist's own 0-based `index`**, the same number `/watch?v=…&list=…&index=` has
 * carried since the queue started linking to playlists. YouTube spells its own `index` 1-based; one
 * app with two meanings for one word would be the worse of the two trades, and an embed address is
 * read by this app rather than handed to YouTube's player.
 *
 * `null` means the list's own beginning, which is what an address that names no entry asks for.
 */
export interface EmbedList {
  readonly playlistId: string;
  readonly index: number | null;
}

/**
 * `listType=` — the kind of list `list` names. Only `playlist` is honoured.
 *
 * `user_uploads` puts a channel *name* in `list` and would need a resolve step before it named
 * anything this app can read; `search` was withdrawn from YouTube's own player years ago. Both
 * answer "Nothing here" rather than being read as a playlist id, because looking a channel name up
 * as a playlist answers a different question and the reader would be told the playlist is missing.
 */
const EMBED_LIST_TYPE = 'playlist';

/** YouTube's `videoseries` — its older spelling of "this embed is a list, not a video". */
const EMBED_VIDEOSERIES = 'videoseries';

/** YouTube's ID shape. Exported because §8's bare-ID fallback (2f) must validate against this one. */
export function isVideoId(value: string): boolean {
  return /^[A-Za-z0-9_-]{11}$/.test(value);
}

/** YouTube handles: `@` plus 3–30 of `[A-Za-z0-9._-]`. Stored and formatted **without** the `@`. */
export function isChannelHandle(value: string): boolean {
  return /^[A-Za-z0-9._-]{3,30}$/.test(value);
}

/**
 * A UCID. Exported because resolving *names* into one has to know when it did not get
 * one: `/api/v1/resolveurl` answers an unrecognised URL with `200`
 * `{"browseId":"FEwhat_to_watch"}` — YouTube's home feed, dressed as a successful resolution — and a
 * playlist with `browseId: "VL<PLID>"`. A `browseId` is therefore not a channel id, and only this
 * shape says which ones are. Measured `UC` + 22 on 14 of 14 resolutions and 10 of 10 channel records.
 */
export function isChannelId(value: string): boolean {
  return /^UC[A-Za-z0-9_-]{22}$/.test(value);
}

/**
 * A legacy custom-URL or username segment — `/c/NAME`, `/user/NAME`. Deliberately looser than
 * {@link isChannelHandle}: these predate handles, YouTube's rules for them changed twice, and the
 * segment is already disambiguated by the literal `c`/`user` in front of it, so a loose rule here
 * cannot swallow another route the way the bare-ID rule could.
 */
export function isChannelNameSegment(value: string): boolean {
  return /^[^\s/?#]{1,100}$/.test(value);
}

/**
 * A clip or community-post id. YouTube's are `Ugkx`-prefixed base64-ish strings — a real clip
 * measured at 36 characters of `[A-Za-z0-9_-]` — but the length is not documented anywhere and the
 * endpoint is the authority on whether one exists, so this checks the alphabet and a floor and
 * nothing else. Same argument as {@link isChannelNameSegment}: the literal `clip`/`post` segment in
 * front already means this rule competes with nothing.
 */
export function isOpaqueEntityId(value: string): boolean {
  return /^[A-Za-z0-9_-]{4,80}$/.test(value);
}

/**
 * A bare vanity segment — `/ayinmaiden`, the legacy custom URL YouTube still answers.
 *
 * The rule is the handle's, because YouTube draws both from the same alphabet and the same length
 * range, and it is named separately because only one of the two is allowed to decide what an
 * *unmatched* path becomes. That is also why it is the handle's tight rule rather than
 * {@link isChannelNameSegment}'s loose one: there is no literal `c` or `user` in front of this form
 * disambiguating it, so a loose rule here would turn every stray path into a network lookup.
 */
export function isBareChannelName(value: string): boolean {
  // No dot, which the handle rule allows and this one cannot: in *this* app's path space a dot means
  // a file, and `/config.json`, `/favicon.ico` and `/sw.js` are all the shape of a name otherwise.
  // A vanity name with a dot in it is still reachable, as `/@name` like every other channel.
  return !value.includes('.') && isChannelHandle(value);
}

/** Which of §8's four "channel by name" spellings a route came in as. */
export type ChannelNameForm = 'handle' | 'c' | 'user' | 'bare';

/**
 * The path prefix each form is written with. `handle` is `@NAME`, `c` and `user` are `form/NAME`, and
 * `bare` has none — which is the whole point of it, and why the round trip has to keep the form: a
 * `/ayinmaiden` rewritten to `/c/ayinmaiden` would be asked of YouTube in a spelling that answers
 * `404` for the very channels this form exists for (measured on `PewDiePie` and `vsauce`, whose
 * custom URLs resolve bare and under `/user/` and not under `/c/`).
 */
function channelNamePrefix(form: ChannelNameForm): string {
  if (form === 'handle') return '@';
  return form === 'bare' ? '' : `${form}/`;
}

function isChannelNameFor(form: ChannelNameForm, name: string): boolean {
  if (form === 'handle') return isChannelHandle(name);
  return form === 'bare' ? isBareChannelName(name) : isChannelNameSegment(name);
}

/**
 * §8's feed pages. `library` is deliberately absent: it is a *spelling* of `playlists` rather than a
 * fifth feed, and canonicalising it in the parser means nothing downstream has to know it exists.
 * `channels` is YouTube's own manage-subscriptions URL — the page a feed's "Manage" button opens —
 * so the domain-swap property holds for it like any other page.
 */
export type FeedKind = 'popular' | 'subscriptions' | 'playlists' | 'history' | 'channels';

const FEED_KINDS: Readonly<Record<string, FeedKind>> = {
  popular: 'popular',
  subscriptions: 'subscriptions',
  playlists: 'playlists',
  history: 'history',
  channels: 'channels',
  // §8: `/feed/library` → `/feed/playlists`. Lossless, so it is canonicalisation and not a redirect
  // — nothing has to be looked up, and the reader keeps the page they asked for.
  library: 'playlists',
  /*
   * The retired Trending screen. Its rows did not go anywhere — they moved to the popular page,
   * above the popular ones and behind a setting — so the URL still means something and says so here
   * rather than through a redirect somewhere in the web app: `parseRoute` answers `popular`,
   * `canonicalHref` therefore answers `/feed/popular`, and the app's one self-redirect effect rewrites
   * the address bar in place. A bookmark, an old landing preference and any shared link keep working,
   * and Android inherits the alias with the table instead of needing its own rule.
   */
  trending: 'popular',
};

export type Route =
  | { readonly kind: 'home' }
  /**
   * The canonical watch route. `startSec` folds §8's four accepted spellings (`t=123`, `t=123s`,
   * `t=1h2m3s`, `start=123`) into one number, because the page seeks — it does not re-parse.
   */
  | {
      readonly kind: 'watch';
      readonly videoId: string;
      readonly startSec: number | null;
      readonly playlistId: string | null;
      readonly index: number | null;
    }
  /**
   * `/embed/ID` — the player alone, its own page since 2026-08-02. It stopped being a
   * spelling of `watch` because it stopped meaning the same thing: an embed is a video *inside
   * somebody else's page*, and canonicalising it onto `/watch` navigated the iframe into the whole
   * app. `startSec` folds the same four spellings the watch arm reads, because `/embed/ID?start=90`
   * is the URL YouTube's own embed writes and domain-swapping it has to keep the position.
   *
   * The rest of what YouTube's embed URL says is {@link EmbedOptions}, in the arm for the same
   * reason `startSec` is: a query parameter is not part of the fixed point, so anything left out
   * here is stripped from the address a moment after the page loads.
   *
   * **`videoId` is `null` exactly when the address names a list and no video** (2026-09-18) —
   * `/embed/?list=PL…&listType=playlist`, which is what a domain-swapped playlist embed from
   * Reddit or a forum post looks like, and `/embed/videoseries?list=PL…`, which is YouTube's older
   * spelling of the same thing. It is the one embed address that cannot be read without asking the
   * instance something: which video the list starts at is the playlist's answer to give, so the
   * screen resolves it and replaces the address with the entry it landed on. `list` and `videoId`
   * are never both absent — `/embed/` with nothing at all stays `not-found`.
   */
  | {
      readonly kind: 'embed';
      readonly videoId: string | null;
      readonly startSec: number | null;
      readonly options: EmbedOptions;
      /** The playlist this embed is walking, and where in it. `null` for a single video. */
      readonly list: EmbedList | null;
    }
  | { readonly kind: 'playlist'; readonly playlistId: string }
  /**
   * §8's four `/feed/*` pages. One arm rather than four because they differ only in *which* feed —
   * the route shape, the nav slot and the sign-in gate are identical — and because the shell needs
   * a real route for every destination it draws: a nav item pointing at a path that parses as
   * `not-found` renders "Nothing here" about a link the app itself wrote.
   */
  | { readonly kind: 'feed'; readonly feed: FeedKind }
  /**
   * §8's channel page. **The tab and the sort are in the route, not in the screen**, for
   * the reason 2c's whole search query is: a tab is a destination somebody links to, the back button
   * has to be what "go back to Videos" means, and a page that holds no state cannot get out of sync
   * with its own URL.
   *
   * `tab: 'videos'` and `sort: 'newest'` format back to a bare `/channel/UCID`, which keeps §8's
   * domain-swap property intact — that is the URL YouTube itself uses, and the jump-out links build it
   * from this arm.
   *
   * The sort is carried for every tab even though only three of the four accept one. That is
   * deliberate and it is 2c's rule again: the route records what was **asked** and
   * `channelTabWireParams` sends only what can be **honoured**, so switching to Playlists and back
   * does not silently discard the reader's sort. It matters more than usual here, because on
   * `/playlists` an unrecognised `sort_by` does not get ignored — it answers `200 []`.
   */
  | {
      readonly kind: 'channel';
      readonly channelId: string;
      readonly tab: ChannelTab;
      readonly sort: ChannelVideoSort;
    }
  /**
   * In-channel search: `/channel/UCID/search?q=…`, backed by
   * `/api/v1/channels/{ucid}/search` — the endpoint that ignores every filter and pages with
   * `page=` (API reference §7f). Its own arm rather than a sixth tab, because it carries a
   * *question*: a tab is a place, and the tab machinery (wire params, per-tab entitlements, the
   * sort) has nothing true to say about it.
   *
   * The text is spelled `q` — the instance's own frontend's spelling — and YouTube's `?query=` is
   * read as an alias, so a pasted YouTube channel-search URL lands here rather than on `not-found`.
   * A `q` that is missing or blank parses as the channel itself: a search with nothing to search
   * for is the channel, and canonicalisation repairs the URL. `page` follows the search route's
   * rule — it is where the accumulated list *starts*.
   */
  | {
      readonly kind: 'channelSearch';
      readonly channelId: string;
      readonly q: string;
      readonly page: number;
    }
  /**
   * A channel named rather than identified — §8's `/@handle`, `/c/NAME` and `/user/NAME`.
   *
   * One arm for all three, because the *form* is the only thing that differs: each needs exactly one
   * `/api/v1/resolveurl` call to become a UCID, and the URL that call takes is
   * `youtubeRouteUrl(route)` — so `links/external.ts`'s premise (our paths *are* YouTube's) doubles
   * as the resolver's input builder and no second table exists to drift.
   *
   * It carries a `tab` for the same reason the `channel` arm does, and because `/@handle/videos` is
   * the URL YouTube itself writes and therefore the one that gets pasted. It carries no `sort`: a
   * sort is a thing you change on a page you are already on, and this arm is never a page.
   */
  | {
      readonly kind: 'channelName';
      readonly form: ChannelNameForm;
      readonly name: string;
      readonly tab: ChannelTab;
    }
  /**
   * `/clip/ID` — §8 wants it resolved to a watch route with the clip's start. That needs
   * `/api/v1/clips/{id}`, which is the only thing that decodes the start out of YouTube's protobuf
   * `params` blob, so it is an arm with a screen rather than a canonicalisation.
   */
  | { readonly kind: 'clip'; readonly clipId: string }
  /**
   * `/post/ID` — a community post. §8: **not** a 404. It parses, it names a real thing, and the page
   * says v1 does not render community posts and offers the jump-out. Deferring the feature and
   * pretending the URL is meaningless are different answers, and only one of them is true.
   */
  | { readonly kind: 'post'; readonly postId: string }
  /** `#tag`, stored and formatted **without** the `#` — the endpoint answers `200 []` for `%23tag`. */
  | { readonly kind: 'hashtag'; readonly tag: string; readonly page: number }
  /**
   * The whole search, not just its text. Every filter is in the URL because a results page
   * has to be shareable and the back button has to return to the filters that produced what you were
   * looking at — the same argument that puts `t` in the watch route.
   *
   * It carries what was **asked**, not what can be honoured. Those differ: a `channel:` operator in
   * `q` forces the other filters back to their defaults on the wire (`effectiveSearchQuery`, and the
   * measurements there for why), but erasing them from the URL as well would mean deleting the
   * operator silently discarded the reader's choices instead of restoring them.
   */
  | { readonly kind: 'search'; readonly search: SearchQuery }
  /**
   * The sign-in page. `/login` is **not** a YouTube path — YouTube signs in on
   * `accounts.google.com` — so this is the one route in the table that is Visious's own rather than a
   * spelling of somebody else's. The path is Invidious's, which is the useful choice: §8's premise is
   * that the two schemes agree, and an instance's own `/login` domain-swaps onto this page instead of
   * onto "Nothing here".
   *
   * `next` is where to go once there is a token — the destination a gated nav item was reaching for
   * when it sent the reader here, so that signing in finishes the journey instead of ending it on a
   * page about signing in. It is a **validated in-app href** rather than a nested `Route`: keeping
   * `Route` non-recursive is the same decision the `redirect` arm was rejected for, and the validation
   * is the point — see {@link safeNextHref}, which refuses a foreign origin the way `channelRouteOf`
   * refuses an absolute `authorUrl`.
   *
   * It is deliberately *not* the URL handed to the instance as `callback_url`; that one carries no
   * query at all. `screens/login/useLogin.ts` says why.
   */
  | { readonly kind: 'login'; readonly next: string | null }
  /**
   * §8's Settings page. Like `/login` it is Visious's own destination rather than a
   * spelling of somebody else's, so it takes the same approach and one step further: **`/settings` is
   * canonical and both of the other schemes' spellings are accepted** — Invidious's `/preferences` and
   * YouTube's `/account`, each canonicalised onto it by {@link canonicalHref}. `/login` made
   * Invidious's path canonical because "login" is also the natural English word for it; here the
   * natural word is "settings" and the two foreign spellings disagree with each other, so neither can
   * be the canonical one without making the other look like an afterthought.
   *
   * **`/preferences` is exactly eleven characters of `[A-Za-z0-9_-]`**, which means that before this
   * arm existed it matched §8's bare-ID fallback and parsed as `watch` for a video called
   * `preferences`. That is the collision the ordering in {@link parseRoute} exists to prevent, and the
   * first time a real path has actually hit it.
   *
   * It carries the topic tab as a path segment (`/settings/interface`) the way the channel page
   * carries its own — a tab is a destination, and Back has to mean "the tab I was on". The controls
   * themselves still edit stored state rather than query parameters: settings are not a filter, they
   * are not shareable, and Back must not undo one. The foreign spellings stay bare — neither scheme
   * has tab segments of its own to honour.
   */
  | { readonly kind: 'settings'; readonly tab: SettingsTab }
  /** Everything unmatched, carrying the path so the screen can say *what* was not found. */
  | { readonly kind: 'not-found'; readonly path: string };

function positiveInt(value: string | null): number | null {
  if (value === null || !/^\d+$/.test(value)) return null;
  const n = Number(value);
  return Number.isSafeInteger(n) ? n : null;
}

/** `1` is yes and anything else is no — how YouTube's own player reads the parameters that are off. */
function isOn(value: string | null): boolean {
  return value === '1';
}

/** The mirror, for the parameters whose default is on: only an explicit `0` turns one off. */
function isOff(value: string | null): boolean {
  return value === '0';
}

/** A BCP-47-shaped tag, loose enough for what an embedder writes and tight enough to round-trip. */
const LANG_TAG_RE = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;

/**
 * What `?list=` and `?listType=` say, read together — three answers rather than a nullable one,
 * because "no list" and "a list this app will not read" have to part company here: the first is an
 * ordinary single-video embed and the second is `not-found`.
 */
type EmbedListRead =
  | { readonly kind: 'none' }
  | { readonly kind: 'list'; readonly list: EmbedList }
  | { readonly kind: 'refused' };

function embedListRead(href: string): EmbedListRead {
  const playlistId = queryParam(href, 'list');
  if (playlistId === null || playlistId === '') return { kind: 'none' };
  // A `listType` this app does not speak is refused rather than ignored — see {@link EMBED_LIST_TYPE}.
  const listType = queryParam(href, 'listType');
  if (listType !== null && listType.toLowerCase() !== EMBED_LIST_TYPE) return { kind: 'refused' };
  return { kind: 'list', list: { playlistId, index: positiveInt(queryParam(href, 'index')) } };
}

/** {@link EmbedOptions}, read off the address. Anything unrecognised is the parameter's own default. */
function embedOptionsOf(href: string): EmbedOptions {
  const captionLanguage = queryParam(href, 'cc_lang_pref');
  // `end=0` is not an end. `positiveInt` reads it, since `start=0` is a real position, but a mark on
  // the first frame would stop the video before it had played anything — so here it is no mark.
  const endSec = positiveInt(queryParam(href, 'end'));
  return {
    autoplay: isOn(queryParam(href, 'autoplay')),
    mute: isOn(queryParam(href, 'mute')),
    loop: isOn(queryParam(href, 'loop')),
    endSec: endSec === 0 ? null : endSec,
    // Every entry validated, so a typo in one id costs that entry rather than the whole sequence.
    playlist: (queryParam(href, 'playlist') ?? '')
      .split(',')
      .map((id) => id.trim())
      .filter((id) => isVideoId(id)),
    controls: !isOff(queryParam(href, 'controls')),
    fullscreen: !isOff(queryParam(href, 'fs')),
    keyboard: !isOn(queryParam(href, 'disablekb')),
    captions: isOn(queryParam(href, 'cc_load_policy')),
    // Lower-cased so one language cannot arrive as two routes; the track match is case-insensitive
    // either way, and this is the spelling `formatRoute` writes back out.
    captionLanguage:
      captionLanguage !== null && LANG_TAG_RE.test(captionLanguage)
        ? captionLanguage.toLowerCase()
        : null,
    // `null` for a language this build has no catalogue for, which is the same answer the settings
    // document gives one: leave the words alone rather than half-translate them.
    uiLanguage: normalizeUiLocale(queryParam(href, 'hl')),
    progressColor: queryParam(href, 'color')?.toLowerCase() === 'white' ? 'white' : null,
  };
}

/**
 * What an embed does when its video ends — `playlist` and `loop`, read together.
 *
 * The sequence advances by **navigating**, which is what makes it survive a reload and lets the
 * address say what is playing. Under `loop` the list is *rotated* rather than drained — the video
 * that just finished goes to the back — so the run comes back round to where it started instead of
 * needing a cursor the URL has no room for. `start` and `end` bracket the video they were written
 * for and do not travel with it.
 *
 * `replay` is the one-video loop, including the way YouTube spells it (`?loop=1&playlist=<this
 * id>`): the rotation lands on the address already in the bar, and navigating there would change
 * nothing, so the player simply starts the video again.
 *
 * A `next` route always carries `autoplay=1`, whatever the address that started the run said. A
 * sequence that arrives at its second entry showing a still frame is a sequence that stopped, and
 * the address has to say so too: without the parameter, reloading the page mid-run would leave the
 * reader on a paused video with no sign that anything was meant to follow.
 */
export type EmbedEnding =
  | { readonly kind: 'stop' }
  | { readonly kind: 'replay' }
  | { readonly kind: 'next'; readonly route: Route }
  /**
   * A `?list=` is playing, so the list answers this and not the address: which entry follows is a
   * fact about the playlist, and core has not read it. {@link embedListStep} does the arithmetic
   * once the screen knows how long the list is, and {@link embedListRoute} writes the address.
   */
  | { readonly kind: 'list' };

export function embedEnding(route: Extract<Route, { kind: 'embed' }>): EmbedEnding {
  // **`list` outranks `playlist`.** Both name what follows, and an address carrying the two is
  // asking one question twice; the playlist is the stronger statement, since it is also what put
  // this video on screen. The `playlist=` ids stay in the address rather than being quietly
  // dropped — they are the embedder's words — and do nothing while a list is running.
  if (route.list !== null) return { kind: 'list' };
  // Unreachable: `list` and `videoId` are never both absent (see the arm). The check is what tells
  // the type that, and what keeps a hand-built route from formatting `/embed/videoseries` with a
  // sequence hanging off it.
  if (route.videoId === null) return { kind: 'stop' };
  const [next, ...rest] = route.options.playlist;
  if (next === undefined) return route.options.loop ? { kind: 'replay' } : { kind: 'stop' };

  const playlist = route.options.loop ? [...rest, route.videoId] : rest;
  const target: Route = {
    kind: 'embed',
    videoId: next,
    startSec: null,
    options: { ...route.options, playlist, endSec: null },
    list: null,
  };
  // Compared *before* the autoplay stamp, so the question stays "is this the address already in the
  // bar" — a one-video loop must answer yes whether or not that address spells `autoplay=1`.
  if (formatRoute(target) === formatRoute(route)) return { kind: 'replay' };
  return { kind: 'next', route: { ...target, options: { ...target.options, autoplay: true } } };
}

/**
 * Where a step through a list embed lands, as an index — the two buttons on the picture, and the
 * advance when a video ends.
 *
 * `null` is "the list has no entry there", which is what disables a button and what stops a run at
 * the last video. **`loop=1` wraps instead**, in both directions: the parameter says the list begins
 * again at its end, and a reader stepping back from the first entry of a looping list is asking for
 * the same wrap the other way.
 *
 * `total` is the playlist's own `videoCount`. A list of one is a list with nowhere to step, and a
 * `total` of zero or less is a playlist that answered nothing — both are `null` rather than a wrap
 * onto the entry already playing.
 */
export function embedListStep(
  list: EmbedList,
  input: { readonly total: number; readonly loop: boolean; readonly direction: 'next' | 'previous' },
): number | null {
  const { total, loop, direction } = input;
  if (!Number.isFinite(total) || total <= 1) return null;
  const last = Math.floor(total) - 1;
  // An address that named no entry is playing the list's first one, which is what it asked for.
  const current = list.index ?? 0;
  if (direction === 'next') {
    if (current < last) return current + 1;
    return loop ? 0 : null;
  }
  if (current > 0) return Math.min(current - 1, last);
  return loop ? last : null;
}

/**
 * The address a step writes: the same list, a new entry, and the video id where the screen already
 * knows it.
 *
 * **`videoId: null` is a real answer rather than a failure** — it formats as
 * `/embed/videoseries?list=…&index=n`, which the screen resolves on arrival the way it resolved the
 * address it started from. That is what makes a step across a page boundary work without reading a
 * second page first: only the entries of the page in hand can be named outright, and the rest are
 * named by their position.
 *
 * `autoplay=1` whatever the address that started the run said, for {@link embedEnding}'s reason: a
 * list that arrives at its next entry on a still frame is a list that stopped. `start` and `end`
 * bracket the video they were written for and do not travel.
 */
export function embedListRoute(
  route: Extract<Route, { kind: 'embed' }> & { readonly list: EmbedList },
  entry: { readonly index: number; readonly videoId: string | null },
): Route {
  return {
    kind: 'embed',
    videoId: entry.videoId,
    startSec: null,
    options: { ...route.options, autoplay: true, endSec: null },
    list: { playlistId: route.list.playlistId, index: entry.index },
  };
}

/**
 * What an embed's caption parameters mean for the player, read against the settings of whoever is
 * watching — `cc_load_policy` and `cc_lang_pref`, which answer two different questions.
 *
 * `cc_load_policy=1` turns subtitles on. `cc_lang_pref` names the language, and on its own it names
 * it for a reader who **already** has subtitles on: an embedder saying which language to prefer is
 * not the same as an embedder asking for subtitles, and turning them on for somebody who left them
 * off would be reading it as one.
 *
 * `playerLanguage` is the fallback for `cc_load_policy=1` with no language named, where the honest
 * reading is "captions this reader can read": the language the player itself is speaking. It only
 * matters for a reader who has no subtitle language of their own.
 */
export function embedSubtitlePref(
  options: EmbedOptions,
  reader: SubtitlePref,
  playerLanguage: string,
): SubtitlePref {
  // A track the reader's own settings would refuse stays refused: an embedder choosing a language
  // is not an embedder overruling "human-authored captions only".
  const allowAutoGenerated = reader.kind === 'lang' ? reader.allowAutoGenerated : true;

  if (options.captions) {
    const code =
      options.captionLanguage ?? (reader.kind === 'lang' ? reader.code : playerLanguage);
    return { kind: 'lang', code, allowAutoGenerated };
  }
  if (options.captionLanguage === null || reader.kind === 'off') return reader;
  return { kind: 'lang', code: options.captionLanguage, allowAutoGenerated };
}

/** §8 — `t` in any of its spellings, else `start`. A malformed `t` is no start time, not zero. */
function startSecOf(href: string): number | null {
  const t = queryParam(href, 't');
  if (t !== null) {
    const parsed = parseTimeParamSeconds(t);
    if (parsed !== null) return parsed;
  }
  return positiveInt(queryParam(href, 'start'));
}

/**
 * The watch arm with every rider read off the href — shared by the four spellings that all mean
 * "play this video": `/watch?v=ID`, `/shorts/ID`, `/live/ID`, `/watch/ID` and a bare `/ID`. §8
 * lists the last four as *redirects*, but they carry the id themselves, so the redirect is a
 * spelling difference rather than a lookup and every one of them can honour a `t`/`start` and a
 * `list` the way the canonical form does — `/live/ID?t=…` is a real link. (`/embed/ID` reads its
 * riders the same way but is now its own arm.)
 */
function watchRouteFrom(videoId: string, href: string): Route {
  return {
    kind: 'watch',
    videoId,
    startSec: startSecOf(href),
    playlistId: queryParam(href, 'list'),
    index: positiveInt(queryParam(href, 'index')),
  };
}

/**
 * A path segment that should be a video id, forgiving a query string glued onto it — and where the
 * riders (`t`, `list`, `index`, `start`) are then to be read from.
 *
 * Some browser extensions rewrite a YouTube watch URL by replacing `?v=` with `/`, which turns
 * `…/watch?v=ID&pp=HASH` into `…/watch/ID&pp=HASH`: the id is right there, followed by what should
 * have been the query string. The reader pasted or clicked a link to a video that exists, so
 * answering "no such page" is the one response that helps nobody, and `canonicalHref` rewrites the
 * address to `/watch?v=ID` from here like it does for every other spelling.
 *
 * `&` is unambiguous in this position: every id shape read out of a path is `[A-Za-z0-9_-]`, and a
 * handle is `[A-Za-z0-9._-]`, so nothing legitimate carries one. What follows it is read as the
 * query it was meant to be, which is what keeps a `&t=90` that came along for the ride — the `pp`
 * that occasioned all this is one of YouTube's own tracking parameters and is dropped like `si`.
 *
 * `null` when the head is not an id, so a segment that merely contains an `&` is still `not-found`.
 */
function videoIdInSegment(
  segment: string,
  href: string,
): { readonly videoId: string; readonly href: string } | null {
  if (isVideoId(segment)) return { videoId: segment, href };
  const glue = segment.indexOf('&');
  if (glue === -1) return null;
  const videoId = segment.slice(0, glue);
  return isVideoId(videoId) ? { videoId, href: `?${segment.slice(glue + 1)}` } : null;
}

/** `/a/b/` → `['a','b']`. Empty segments are dropped, so a trailing slash never changes the route. */
function segmentsOf(path: string): string[] {
  const out: string[] = [];
  for (const raw of path.split('/')) {
    if (raw === '') continue;
    try {
      out.push(decodeURIComponent(raw));
    } catch {
      out.push(raw);
    }
  }
  return out;
}

/**
 * Reads a route out of an in-app href. The origin is ignored when one is present, so both
 * `/watch?v=…` and `https://tube.example/watch?v=…` read the same — deciding whether a *foreign*
 * URL belongs to us at all is `resolveUrl`'s job (2f), not this function's.
 *
 * Total: anything unrecognised comes back as `not-found` carrying the path.
 */
export function parseRoute(href: string): Route {
  // `parseUrl('')` is `null` by design (an empty string is not a URL), but an empty *href* on this
  // origin is the root — an anchor written `href=""` reloads the current page, not nothing.
  if (href.trim() === '') return { kind: 'home' };

  const parts = parseUrl(href);
  if (parts === null) return { kind: 'not-found', path: href };

  const segments = segmentsOf(parts.path);
  const path = `/${segments.join('/')}`;

  if (segments.length === 0) return { kind: 'home' };

  const [first, second] = segments;

  if (segments.length === 1 && first === 'watch') {
    const videoId = queryParam(href, 'v');
    if (videoId !== null && isVideoId(videoId)) return watchRouteFrom(videoId, href);
    // A `/watch` with no usable `v` is a broken link, not the home page. Saying so beats
    // redirecting somewhere plausible and leaving the reader to wonder what they clicked.
    return { kind: 'not-found', path };
  }

  if (segments.length === 1 && first === 'playlist') {
    const playlistId = queryParam(href, 'list');
    if (playlistId !== null && playlistId !== '') return { kind: 'playlist', playlistId };
    return { kind: 'not-found', path };
  }

  if (segments.length === 1 && (first === 'results' || first === 'search')) {
    // §8: accept `search_query` and `q` interchangeably, because YouTube writes the first and
    // Invidious the second, and domain-swapping either one has to work.
    const q = queryParam(href, 'search_query') ?? queryParam(href, 'q');
    if (q === null || q.trim() === '') return { kind: 'not-found', path };
    return {
      kind: 'search',
      search: {
        q,
        // Every filter through its normaliser, so a hand-edited or foreign URL cannot put a value on
        // the wire that this build has not verified. The API validates none of them: `type=bogus`,
        // `features=notathing` and `page=abc` all answer 200 (`search/query.ts`).
        type: normalizeSearchType(queryParam(href, 'type')),
        sort: normalizeSearchSort(queryParam(href, 'sort')),
        date: normalizeSearchDate(queryParam(href, 'date')),
        duration: normalizeSearchDuration(queryParam(href, 'duration')),
        features: normalizeSearchFeatures(queryParam(href, 'features')),
        page: normalizeSearchPage(queryParam(href, 'page')),
      },
    };
  }

  if (segments.length >= 2 && segments.length <= 3 && first === 'channel' && second !== undefined && second !== '') {
    const third = withoutSelfPath(segments[2]);
    if (third === 'search') {
      // `q` is ours and the instance frontend's; `query` is YouTube's own channel-search spelling.
      const q = queryParam(href, 'q') ?? queryParam(href, 'query');
      if (q === null || q.trim() === '') {
        // A search with nothing to search for is the channel itself — canonicalised, not refused.
        return {
          kind: 'channel',
          channelId: second,
          tab: DEFAULT_CHANNEL_TAB,
          sort: normalizeChannelSort(queryParam(href, 'sort')),
        };
      }
      return {
        kind: 'channelSearch',
        channelId: second,
        q,
        page: normalizeSearchPage(queryParam(href, 'page')),
      };
    }
    // `/channel/UCID/notatab` is `not-found` rather than the videos tab. It has to be: the API answers
    // an unknown sub-path with **`200` and zero bytes**, so silently canonicalising a typo would turn a
    // bad link into an apparently-empty channel.
    if (third !== undefined && !isChannelTab(third)) return { kind: 'not-found', path };
    return {
      kind: 'channel',
      channelId: second,
      tab: third === undefined ? DEFAULT_CHANNEL_TAB : third,
      sort: normalizeChannelSort(queryParam(href, 'sort')),
    };
  }

  if (segments.length === 2 && first === 'hashtag' && second !== undefined && second !== '') {
    // A leading `#` is stripped rather than refused: `/hashtag/%23lofi` is what a naive link builder
    // writes, and the API answers `200 {"results":[]}` for it — an empty page with nothing saying why.
    const tag = second.startsWith('#') ? second.slice(1) : second;
    if (tag === '') return { kind: 'not-found', path };
    return { kind: 'hashtag', tag, page: normalizeSearchPage(queryParam(href, 'page')) };
  }

  if (segments.length === 1 && first === 'login') {
    return { kind: 'login', next: safeNextHref(queryParam(href, 'next')) };
  }

  // §8's Settings page and the two foreign spellings of it. **Before the bare-ID fallback**, which is
  // not a stylistic ordering: `preferences` is eleven characters of the ID alphabet, so the fallback
  // would otherwise claim it as a video (see the `settings` arm).
  if (segments.length === 1 && first !== undefined && SETTINGS_PATH_ALIASES.has(first)) {
    return { kind: 'settings', tab: DEFAULT_SETTINGS_TAB };
  }

  // The topic tabs, canonical spelling only. An unknown segment is `not-found` for the channel
  // page's reason: canonicalising a typo would land on a tab nobody asked for.
  if (segments.length === 2 && first === 'settings' && second !== undefined) {
    if (isSettingsTab(second)) return { kind: 'settings', tab: second };
    return { kind: 'not-found', path };
  }

  if (segments.length === 2 && first === 'feed' && second !== undefined) {
    const feed = FEED_KINDS[second];
    // An unknown feed name is `not-found` on purpose: `/feed/anything` silently becoming a page
    // would make a typo look like an empty subscription list.
    if (feed !== undefined) return { kind: 'feed', feed };
    return { kind: 'not-found', path };
  }

  // §8's three two-segment spellings of the watch route. Canonicalised rather than redirected,
  // because the id is right there in the path — see {@link watchRouteFrom}.
  //
  // `/shorts/ID` is the one that *has* to be local, and that is a measurement rather than a
  // preference: `/api/v1/resolveurl` cannot turn a Shorts URL into a video id. A real Short answers
  // `200 {"params":null,"pageType":"WEB_PAGE_TYPE_SHORTS"}` — no `videoId` field at all — and a
  // `/shorts/` URL whose id is an ordinary video answers `400 {"error":"Unknown url"}` (2 of 3 ids
  // tried). A resolver-first design would therefore break the single most common inbound link.
  if (segments.length === 2 && second !== undefined && WATCH_PATH_ALIASES.has(first ?? '')) {
    const found = videoIdInSegment(second, href);
    if (found !== null) return watchRouteFrom(found.videoId, found.href);
    return { kind: 'not-found', path };
  }

  /*
   * `/embed/ID` was the fourth spelling of `watch` until it got its own page — see the `embed` arm.
   *
   * Three addresses land here and the second segment is what tells them apart: an id is the video,
   * `videoseries` is YouTube's own "this is a list", and **nothing at all** is the spelling an
   * embedder writes today (`/embed/?list=PL…&listType=playlist`, which is what a Reddit post's
   * player turns into when the host is swapped for this one). The last two need a `list` to mean
   * anything, so without one they stay `not-found` — `/embed/` alone is a truncated address rather
   * than a page.
   */
  if (segments.length <= 2 && first === 'embed') {
    // **`videoseries` is checked before the id, because it *is* one.** The word is eleven characters
    // of the id alphabet, so reading the segment first would route YouTube's own name for "this is a
    // list" as a video that does not exist. It is reserved in this one path segment only.
    const namesAList = second === undefined || second === EMBED_VIDEOSERIES;
    const found = namesAList || second === undefined ? null : videoIdInSegment(second, href);
    // The glued-query form puts the real query on `found.href` — read every rider off the same one.
    const riders = found?.href ?? href;
    const list = embedListRead(riders);
    if (list.kind === 'refused') return { kind: 'not-found', path };
    if (found === null) {
      if (!namesAList || list.kind !== 'list') return { kind: 'not-found', path };
      return {
        kind: 'embed',
        videoId: null,
        startSec: startSecOf(riders),
        options: embedOptionsOf(riders),
        list: list.list,
      };
    }
    return {
      kind: 'embed',
      videoId: found.videoId,
      startSec: startSecOf(riders),
      options: embedOptionsOf(riders),
      list: list.kind === 'list' ? list.list : null,
    };
  }

  if (segments.length === 2 && first === 'clip' && second !== undefined) {
    if (isOpaqueEntityId(second)) return { kind: 'clip', clipId: second };
    return { kind: 'not-found', path };
  }

  if (segments.length === 2 && first === 'post' && second !== undefined) {
    if (isOpaqueEntityId(second)) return { kind: 'post', postId: second };
    return { kind: 'not-found', path };
  }

  // `/c/NAME`, `/user/NAME` and `/@handle`, each optionally followed by a channel tab — the URL
  // YouTube writes is `/@handle/videos`, so that is the one that gets pasted. An unrecognised third
  // segment is `not-found` for the same reason `/channel/UCID/notatab` is: canonicalising a typo
  // would resolve a name and then land on a tab nobody asked for.
  if (segments.length >= 2 && segments.length <= 3 && (first === 'c' || first === 'user')) {
    return channelNameRoute(first, segments[1], segments[2], path);
  }

  if (segments.length >= 1 && segments.length <= 2 && first !== undefined && first.startsWith('@')) {
    return channelNameRoute('handle', first.slice(1), segments[1], path);
  }

  // §8's bare-ID fallback, **last** and validated exactly. It is what makes swapping the domain on a
  // `youtu.be/ID` link work — that host puts the id in the path, and `/api/v1/resolveurl` refuses a
  // `youtu.be` URL outright (`400 {"error":"Unknown url"}`), so there is no other way to read one.
  if (segments.length === 1 && first !== undefined) {
    const found = videoIdInSegment(first, href);
    if (found !== null) return watchRouteFrom(found.videoId, found.href);
  }

  /*
   * The legacy custom URL, `/ayinmaiden` — **after** the id rule and after every route above, so it
   * claims only what nothing else did (2026-09-06).
   *
   * It is the last thing tried because it is the only arm that cannot be answered here: it costs a
   * request, and a wrong guess costs the reader that request before a 404. What it buys is the form
   * YouTube itself still serves and the one creators still print on merchandise, which until now was
   * the one channel spelling of the four that answered "Nothing here".
   *
   * **The eleven-character collision is decided for the video**, and knowingly: `/TrishasLife` is
   * eleven characters of the id alphabet and stays a watch route, because the bare-ID rule is what
   * makes a `youtu.be` link work under this domain and that is the commoner link by far. A vanity
   * name of exactly eleven id characters is therefore still unreachable in this spelling, and reachable
   * as `/@name` like any other.
   *
   * A tab may follow it (`/ayinmaiden/streams`), which is what keeps `formatRoute` a fixed point for
   * every tab rather than only the default one.
   */
  if (segments.length >= 1 && segments.length <= 2 && first !== undefined && !RESERVED_PATHS.has(first)) {
    return channelNameRoute('bare', first, segments[1], path);
  }

  return { kind: 'not-found', path };
}

/**
 * The three spellings of the settings page. `settings` is in the set as well as being the canonical
 * form, so the rule is one lookup rather than "the canonical one plus a list of others".
 */
const SETTINGS_PATH_ALIASES: ReadonlySet<string> = new Set([
  'settings',
  // Invidious's own path, so an instance URL domain-swaps onto this page (§8's premise, the same
  // reason `/login` is spelled Invidious's way).
  'preferences',
  // YouTube's. `youtube.com/account` is where its settings live, and §8's goal is that swapping the
  // host in any YouTube URL lands on the matching page.
  'account',
]);

const WATCH_PATH_ALIASES: ReadonlySet<string> = new Set([
  'shorts',
  'live',
  // `embed` left this set when it got its own page (2026-08-02) — see the `embed` arm.
  // Not a YouTube form. §8 tolerates it under the self-redirect requirement, and it is what a human
  // writes from memory.
  'watch',
]);

/**
 * The words this app's own URL space already uses, held back from the vanity-name arm at the end of
 * {@link parseRoute} so they keep answering "Nothing here" rather than costing a request.
 *
 * A single segment that is one of these is a **truncated in-app URL** — somebody hand-edited
 * `/channel/UC…` down to `/channel`, or typed `/feed` and forgot which one — and looking it up would
 * answer them about a channel named "feed". Every one of them is the first segment of a route that
 * needs a second one; the forms that are already complete at one segment (`/watch`, `/settings`,
 * `/login`) never reach that arm, because their own branch answers first.
 *
 * `queue` is in for a neighbouring reason rather than the same one: it is a path this app owns and
 * deliberately has no page for, since a queue is drawn in the watch page's side panel and lives in one
 * tab's `sessionStorage`.
 */
const RESERVED_PATHS: ReadonlySet<string> = new Set([
  'channel',
  'feed',
  'clip',
  'post',
  'embed',
  'c',
  'user',
  'queue',
  ...WATCH_PATH_ALIASES,
]);


/**
 * Two of YouTube's channel sub-paths, dropped so the URL reads as the channel itself.
 *
 * **`/join`** is the channel-membership page: a paid tier of somebody else's account, sold through a
 * system this build is not a client of and has no screen for. **`/featured`** is YouTube's own name
 * for the channel's front page, which is where a bare channel URL already lands.
 *
 * Either way what the link names is still the channel, so `youtube.com/@name/join` in a description
 * opens the channel here instead of leaving for YouTube, and both format back to `/@name` so the
 * address bar says where the reader landed.
 *
 * A strip rather than two more `ChannelTab`s: one has nothing to show and the other is a tab this
 * build already has under a different name. And it does not reopen the door `/channel/UCID/notatab`
 * closes, because these are exempted **by name**: a typo still answers `not-found`, since silently
 * canonicalising one turns a bad link into an apparently-empty channel.
 */
const CHANNEL_SELF_PATHS: ReadonlySet<string> = new Set(['join', 'featured']);

function withoutSelfPath(segment: string | undefined): string | undefined {
  return segment !== undefined && CHANNEL_SELF_PATHS.has(segment) ? undefined : segment;
}

function channelNameRoute(
  form: ChannelNameForm,
  name: string | undefined,
  tabSegment: string | undefined,
  path: string,
): Route {
  if (name === undefined || !isChannelNameFor(form, name)) return { kind: 'not-found', path };
  const tab = withoutSelfPath(tabSegment);
  if (tab !== undefined && !isChannelTab(tab)) return { kind: 'not-found', path };
  return {
    kind: 'channelName',
    form,
    name,
    tab: tab === undefined ? DEFAULT_CHANNEL_TAB : tab,
  };
}

function query(pairs: readonly (readonly [string, string | number | null])[]): string {
  const parts: string[] = [];
  for (const [key, value] of pairs) {
    if (value === null || value === '') continue;
    parts.push(`${key}=${encodeURIComponent(String(value))}`);
  }
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

/**
 * The inverse of {@link parseRoute}, and the only way the app builds an in-app href — a hand-written
 * `` `/watch?v=${id}` `` somewhere in a component is how the two halves drift apart.
 *
 * `parseRoute(formatRoute(r))` returns `r` for every arm; the testkit asserts it per arm rather
 * than trusting the symmetry to hold by inspection.
 */
export function formatRoute(route: Route): string {
  switch (route.kind) {
    case 'home':
      return '/';
    case 'watch':
      return `/watch${query([
        ['v', route.videoId],
        ['t', route.startSec],
        ['list', route.playlistId],
        ['index', route.index],
      ])}`;
    case 'embed': {
      // `start`, not `t` — YouTube's own embed spelling, so a domain-swapped embed URL is already
      // canonical and `youtubeRouteUrl(route)` is a URL YouTube's player actually honours. Every
      // option below is spelled the way YouTube spells it, and omitted at its own default so the
      // address says only what was asked for.
      const options = route.options;
      // `videoseries` for a list with no video of its own, which is the spelling YouTube's own player
      // writes and therefore the one a domain swap arrives as. `listType=playlist` is not written
      // back: the segment already says it, and an unset parameter formats to nothing.
      const segment = route.videoId === null ? EMBED_VIDEOSERIES : encodeURIComponent(route.videoId);
      return `/embed/${segment}${query([
        ['start', route.startSec],
        ['list', route.list?.playlistId ?? null],
        ['index', route.list?.index ?? null],
        ['end', options.endSec],
        ['autoplay', options.autoplay ? '1' : null],
        ['mute', options.mute ? '1' : null],
        ['loop', options.loop ? '1' : null],
        ['playlist', options.playlist.length === 0 ? null : options.playlist.join(',')],
        ['controls', options.controls ? null : '0'],
        ['fs', options.fullscreen ? null : '0'],
        ['disablekb', options.keyboard ? null : '1'],
        ['cc_load_policy', options.captions ? '1' : null],
        ['cc_lang_pref', options.captionLanguage],
        ['hl', options.uiLanguage],
        ['color', options.progressColor],
      ])}`;
    }
    case 'playlist':
      return `/playlist${query([['list', route.playlistId]])}`;
    case 'feed':
      return `/feed/${route.feed}`;
    case 'channel': {
      // The default tab formats to the bare path — YouTube's own URL, and the one a domain swap has to
      // produce. The sort is a query parameter rather than a fourth segment because it is not a
      // destination, and because omitting it at the default keeps the shareable URL short.
      const tab = route.tab === DEFAULT_CHANNEL_TAB ? '' : `/${route.tab}`;
      return `/channel/${encodeURIComponent(route.channelId)}${tab}${query([
        ['sort', route.sort === DEFAULT_CHANNEL_SORT ? null : route.sort],
      ])}`;
    }
    case 'channelSearch':
      // `q` written, `query` merely read — the arm's own comment says whose spelling is whose.
      return `/channel/${encodeURIComponent(route.channelId)}/search${query([
        ['q', route.q],
        ['page', route.page === SEARCH_FIRST_PAGE ? null : route.page],
      ])}`;
    case 'channelName': {
      // The same shape as the `channel` arm above: the default tab formats to the bare path, which is
      // the URL YouTube itself uses and therefore the one a domain swap has to produce.
      const tab = route.tab === DEFAULT_CHANNEL_TAB ? '' : `/${route.tab}`;
      return `/${channelNamePrefix(route.form)}${encodeURIComponent(route.name)}${tab}`;
    }
    case 'clip':
      return `/clip/${encodeURIComponent(route.clipId)}`;
    case 'post':
      return `/post/${encodeURIComponent(route.postId)}`;
    case 'hashtag':
      return `/hashtag/${encodeURIComponent(route.tag)}${query([
        ['page', route.page === SEARCH_FIRST_PAGE ? null : route.page],
      ])}`;
    case 'search': {
      const s = route.search;
      // Defaults omitted, so `/results?search_query=lofi` is what an unfiltered search looks like —
      // short enough to read, and identical to the URL YouTube itself would have been given. Note
      // this is the **asked-for** query rather than `effectiveSearchQuery`: see the `search` arm.
      return `/results${query([
        ['search_query', s.q],
        ['type', s.type === DEFAULT_SEARCH_QUERY.type ? null : s.type],
        ['sort', s.sort === DEFAULT_SEARCH_QUERY.sort ? null : s.sort],
        ['date', s.date === DEFAULT_SEARCH_QUERY.date ? null : s.date],
        ['duration', s.duration === DEFAULT_SEARCH_QUERY.duration ? null : s.duration],
        ['features', s.features.length === 0 ? null : s.features.join(',')],
        ['page', s.page === SEARCH_FIRST_PAGE ? null : s.page],
      ])}`;
    }
    case 'login':
      return `/login${query([['next', route.next]])}`;
    case 'settings':
      // The default tab formats back to the bare path, the channel page's own rule — so the three
      // spellings keep canonicalising onto `/settings` rather than onto a longer synonym of it.
      return route.tab === DEFAULT_SETTINGS_TAB ? '/settings' : `/settings/${route.tab}`;
    case 'not-found':
      return route.path;
  }
}

/**
 * A `?next=` value that is safe to navigate to, or `null`.
 *
 * The rule is the one `channelRouteOf` applies to `authorUrl`: **parse the input rather than trusting
 * it.** A return-to parameter is the classic open-redirect hole, and this app is unusually exposed to
 * it because `parseRoute` ignores origins on purpose — `https://evil.example/watch?v=…` reads as a
 * perfectly good watch route, so "does it parse?" is not the question. Four refusals, in order:
 *
 * - an **absolute** URL, which is the attack;
 * - anything not starting with `/`, which cannot be a path on this origin;
 * - `not-found`, which also disposes of `//evil.example/x` — a protocol-relative URL is not absolute
 *   to {@link parseUrl} (it refuses a scheme-less authority) but it does not parse either;
 * - another `/login`, because `?next=/login` is a loop with a friendly face.
 *
 * What comes back is {@link formatRoute}'s spelling rather than the caller's, so `?next=/shorts/ID`
 * becomes `?next=/watch?v=ID` and the whole href stays a fixed point of `formatRoute ∘ parseRoute` —
 * which is what lets {@link canonicalHref} run on this route like any other.
 */
export function safeNextHref(value: string | null): string | null {
  if (value === null || value.trim() === '') return null;
  if (parseUrl(value)?.absolute === true) return null;
  if (!value.startsWith('/')) return null;

  const route = parseRoute(value);
  if (route.kind === 'not-found' || route.kind === 'login') return null;
  return formatRoute(route);
}

/**
 * The sign-in route, optionally carrying where the reader was heading. Every gated destination builds
 * its link with this, so "come back here afterwards" is one rule rather than a query parameter each
 * call site remembers to add.
 */
export function loginRoute(next: Route | null = null): Route {
  // `not-found` is refused here rather than left to the round trip through `safeNextHref`: its `path`
  // is the reader's unreadable input, and since the vanity-name arm (2026-09-06) a single unmatched
  // segment reads back as a channel — so formatting it and parsing it again would turn "that link is
  // broken" into a lookup for a channel with that name.
  if (next === null || next.kind === 'not-found') return { kind: 'login', next: null };
  return { kind: 'login', next: safeNextHref(formatRoute(next)) };
}

/**
 * The canonical spelling of an href, or `null` when it already is one.
 *
 * This is §8's whole "self-redirect of unsupported YT forms" requirement in five lines, and it covers
 * every non-canonical spelling at once rather than one rule per row: `/shorts/ID` and a bare `/ID`
 * become `/watch?v=ID`, `/feed/library` becomes `/feed/playlists`, `/preferences` and `/account`
 * become `/settings`, `/search?q=x` becomes
 * `/results?search_query=x`, a trailing slash goes, `?si=`-style tracking parameters that no arm
 * carries are dropped, and a URL that spells its defaults out loses them. Anything the app should
 * *not* rewrite answers `null` by construction:
 *
 * - `not-found` is refused explicitly — the path is unreadable, so there is nothing to canonicalise
 *   into and rewriting it would destroy the one piece of evidence the 404 page has to show.
 * - `channelName`, `clip` and `post` format back to themselves, so they stay put while their screen
 *   does the lookup. The *result* of that lookup is a navigation, which is a different mechanism.
 *
 * **It terminates because `parseRoute(formatRoute(r))` is `r` for every arm** — the round-trip
 * property the testkit already asserts per arm. That makes `formatRoute`'s output a fixed point, so a
 * caller that replaces the URL and re-reads it gets `null` the second time rather than a loop. The
 * testkit asserts that directly for every redirect form, because "the redirect table cannot cycle" is
 * too load-bearing to hold only by inference.
 *
 * The comparison is against the incoming path and query with the origin and the fragment removed,
 * which is exactly the shape {@link formatRoute} emits.
 */
export function canonicalHref(href: string): string | null {
  const route = parseRoute(href);
  if (route.kind === 'not-found') return null;

  const parts = parseUrl(href);
  if (parts === null) return null;
  const incoming = `${parts.path === '' ? '/' : parts.path}${parts.query === '' ? '' : `?${parts.query}`}`;

  const canonical = formatRoute(route);
  return canonical === incoming ? null : canonical;
}

/**
 * A search route from text plus whatever is being changed — so the six defaults are written once here
 * rather than at every call site that only cares about `q` (the header box) or about one filter (a
 * chip in the results page). Changing a filter resets `page` unless the caller says otherwise, which
 * is the behaviour every faceted list wants and the one that is a bug when it is forgotten: page 4 of
 * "videos" is not page 4 of "playlists".
 */
export function searchRoute(q: string, overrides: Partial<Omit<SearchQuery, 'q'>> = {}): Route {
  return {
    kind: 'search',
    search: { ...DEFAULT_SEARCH_QUERY, q, ...overrides },
  };
}

/**
 * A channel route at its defaults — the videos tab, newest first.
 *
 * It exists so that adding the tab and the sort in 2d did not turn nine call sites into nine places
 * that have to remember what the defaults are. Every caller that only knows a UCID wants this one; a
 * caller that means a specific tab writes the arm out.
 */
export function channelRoute(
  channelId: string,
  overrides: { readonly tab?: ChannelTab; readonly sort?: ChannelVideoSort } = {},
): Route {
  return {
    kind: 'channel',
    channelId,
    tab: overrides.tab ?? DEFAULT_CHANNEL_TAB,
    sort: overrides.sort ?? DEFAULT_CHANNEL_SORT,
  };
}

/**
 * A `/@handle` route at its default tab — {@link channelRoute}'s counterpart for the half of the
 * scheme that has a name instead of an id.
 */
export function handleRoute(name: string, overrides: { readonly tab?: ChannelTab } = {}): Route {
  return {
    kind: 'channelName',
    form: 'handle',
    name,
    tab: overrides.tab ?? DEFAULT_CHANNEL_TAB,
  };
}

/**
 * The route a description's `mention` node points at. Both of its targets are nullable (see
 * `model/richText.ts`), and which one is present decides whether the link routes directly or has to
 * go through handle resolution — so the choice belongs here rather than in a renderer.
 */
export function mentionRoute(target: {
  readonly channelId: string | null;
  readonly handle: string | null;
}): Route | null {
  if (target.channelId !== null && target.channelId !== '') {
    return channelRoute(target.channelId);
  }
  if (target.handle !== null && isChannelHandle(target.handle)) {
    return handleRoute(target.handle);
  }
  return null;
}

/**
 * The channel a `VideoSummary` / `VideoDetails` / `Comment` byline links to. Three screens derived
 * this by hand before this and all three had the same hole in them, which is why it is one
 * function now.
 *
 * `authorId` first, `authorUrl` only as a fallback — and the fallback is **parsed, not trusted**.
 * A YouTube collaboration video has no single author, and the API says so in a shape that reads as
 * data: `author: "Big Think Clips and 3 more"`, `authorId: ""` and — the trap —
 * `authorUrl: "/channel/"`. That is a *non-empty* string, so it survives every "is it missing?"
 * check on the way here and then routes to nothing. Measured at 6 of 96 `recommendedVideos` records
 * across eight live videos, so it is a normal state rather than an edge case.
 *
 * A route this function refuses means "this video has no one channel", which is the truth, and the
 * caller renders the author as text. Returning a dead link instead would put a byline on the page
 * that navigates to "Nothing here".
 */
export function channelRouteOf(target: {
  readonly authorId: string | null;
  readonly authorUrl: string | null;
}): Route | null {
  const id = target.authorId?.trim() ?? '';
  if (id !== '') return channelRoute(id);

  if (target.authorUrl === null) return null;
  // `authorUrl` is Invidious's own **relative** link — 430 of 430 values across the whole fixture
  // corpus and eight live responses. `parseRoute` ignores an origin by design (that judgement is
  // 2f's `resolveUrl`), so without this line a foreign absolute URL would still hand back an in-app
  // channel route read out of its path. Refusing input we do not understand is the cheaper mistake.
  if (parseUrl(target.authorUrl)?.absolute === true) return null;

  // Only the two arms that name a channel. `/@handle` costs a `resolveurl` call to become a UCID, and
  // since 2f that call has a screen — so the byline links to the channel either way.
  const route = parseRoute(target.authorUrl);
  return route.kind === 'channel' || route.kind === 'channelName' ? route : null;
}
